import { clearCache, getCache, setCache } from '@/lib/utils';
import { countCharactersInPlateContent, countCharactersWithSpacesInPlateContent, countWordsInPlateContent } from '@/utils/word-count';
import { supabase } from './supabase';

/**
 * Interface for Chapter data
 */
export interface Chapter {
  id: number;
  created_at: string;
  story_id: number;
  tenant_id: string;
  title: string;
  name?: string; // For compatibility with timeline interface
  description?: string;
  content_url?: string;
  content_type?: string;
  version?: number;
  last_saved_at?: string;
  display_order?: number;
}

export interface ChapterWithStats extends Chapter {
  word_count: number;
}

/**
 * Interface for Chapter Content data
 */
export interface ChapterContent {
  id: number;
  created_at: string;
  updated_at: string;
  chapter_id: number;
  tenant_id: string;
  content: any; // JSONB content for PlateJS
  version: number;
  word_count?: number;
  character_count?: number;
  character_count_with_spaces?: number;
  is_active?: boolean;
}

/**
 * Interface for Chapter Statistics
 */
export interface ChapterStats {
  id: number;
  chapter_id: number;
  last_updated_at: string;
  word_count?: number;
  character_count?: number;
  reading_time?: number;
}



/**
 * Interface for Export Configuration
 */
export interface ExportConfiguration {
  id: number;
  tenant_id: string;
  story_id: number;
  name: string;
  format: string;
  configuration: any;
  created_at: string;
  updated_at: string;
}

// Cache keys
const CHAPTERS_CACHE_PREFIX = 'chapters_story_';
const CHAPTER_CONTENT_CACHE_PREFIX = 'chapter_content_';
const CHAPTER_STATS_CACHE_PREFIX = 'chapter_stats_';

const EXPORT_CONFIG_CACHE_PREFIX = 'export_config_story_';

/**
 * Fetch all chapters for a story
 * @param storyId - The ID of the story
 * @returns Promise with array of chapters
 */
export const getChaptersByStoryId = async (storyId: number): Promise<Chapter[]> => {
  // Try to get from cache first
  const cacheKey = `${CHAPTERS_CACHE_PREFIX}${storyId}`;
  const cachedChapters = getCache<Chapter[]>(cacheKey);

  if (cachedChapters) {
    return cachedChapters;
  }

  // If not in cache, fetch from API
  const { data, error } = await supabase
    .from('chapters')
    .select('*')
    .eq('story_id', storyId)
    .order('display_order', { ascending: true });

  if (error) {
    console.error('Error fetching chapters:', error);
    throw error;
  }

  // Cache the result (5 minutes)
  setCache(cacheKey, data || [], 5 * 60 * 1000);

  return data || [];
};

/**
 * Fetch all chapters for a project (across all stories)
 * @param projectId - The ID of the project
 * @returns Promise with array of chapters
 */
export const getChaptersByProjectId = async (projectId: number): Promise<Chapter[]> => {
  // First get all stories for the project
  const { data: stories, error: storiesError } = await supabase
    .from('stories')
    .select('id')
    .eq('project_id', projectId);

  if (storiesError) {
    console.error('Error fetching stories for project:', storiesError);
    throw storiesError;
  }

  if (!stories || stories.length === 0) {
    return [];
  }

  const storyIds = stories.map(story => story.id);

  // Get all chapters for these stories
  const { data, error } = await supabase
    .from('chapters')
    .select('*')
    .in('story_id', storyIds)
    .order('story_id', { ascending: true })
    .order('display_order', { ascending: true });

  if (error) {
    console.error('Error fetching chapters for project:', error);
    throw error;
  }

  return data || [];
};

/**
 * Fetch all chapters for a story with stats (word count) using RPC
 * @param storyId - The ID of the story
 * @returns Promise with array of chapters with stats
 */
export const getChaptersWithStats = async (storyId: number): Promise<ChapterWithStats[]> => {
  const { data, error } = await supabase.rpc('get_story_chapters', { p_story_id: storyId });

  if (error) {
    console.error('Error fetching chapters with stats:', error);
    throw error;
  }

  return data || [];
};

/**
 * Create a new chapter
 * @param storyId - The ID of the story
 * @param title - The title of the chapter
 * @param description - Optional description of the chapter
 * @returns Promise with the created chapter
 */
export const createChapter = async (storyId: number, title: string, description?: string): Promise<Chapter> => {
  try {
    console.log('=== Creating chapter with params:', { storyId, title });

    // First check if we're authenticated
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      console.error('=== Authentication error:', authError);
      throw new Error('Not authenticated');
    }
    console.log('=== Authenticated as user:', user.id);

    // Check if user has a tenant association
    const { data: tenantData, error: tenantError } = await supabase
      .from('user_tenants')
      .select('tenant_id')
      .eq('user_id', user.id)
      .single();

    if (tenantError) {
      console.error('=== Error fetching tenant:', tenantError);
      throw new Error('No tenant association found');
    }
    console.log('=== User tenant:', tenantData?.tenant_id);

    // Now try to create the chapter
    const { data, error } = await supabase
      .from('chapters')
      .insert([
        {
          story_id: storyId,
          title: title,
          description: description,
          // tenant_id is automatically set by RLS policies using auth.jwt()
        }
      ])
      .select()
      .single();

    if (error) {
      console.error('=== Error creating chapter:', error);
      throw error;
    }

    console.log('=== Chapter created successfully:', data);

    // Clear the chapters cache for this story
    clearCache(`${CHAPTERS_CACHE_PREFIX}${storyId}`);

    return data;
  } catch (error) {
    console.error('=== Error in createChapter:', error);
    throw error;
  }
};

/**
 * Get a chapter by ID
 * @param chapterId - The ID of the chapter
 * @returns Promise with the chapter
 */
export const getChapterById = async (chapterId: number): Promise<Chapter> => {
  const { data, error } = await supabase
    .from('chapters')
    .select('*')
    .eq('id', chapterId)
    .single();

  if (error) {
    console.error('Error fetching chapter:', error);
    throw error;
  }

  return data;
};

/**
 * Update a chapter's title and description
 * @param chapterId - The ID of the chapter
 * @param title - The new title
 * @param description - The new description (optional)
 * @returns Promise with the updated chapter
 */
export const updateChapterTitle = async (chapterId: number, title: string, description?: string): Promise<Chapter> => {
  const updateData: { title: string; description?: string } = { title };
  if (description !== undefined) {
    updateData.description = description;
  }

  const { data, error } = await supabase
    .from('chapters')
    .update(updateData)
    .eq('id', chapterId)
    .select()
    .single();

  if (error) {
    console.error('Error updating chapter:', error);
    throw error;
  }

  // Clear any chapter caches that might contain this chapter
  // Since we don't know the story ID here, we'll rely on cache expiration

  return data;
};

/**
 * Get chapter content
 * @param chapterId - The ID of the chapter
 * @returns Promise with the chapter content
 */
export const getChapterContent = async (chapterId: number): Promise<ChapterContent> => {
  // Try to get from cache first
  const cacheKey = `${CHAPTER_CONTENT_CACHE_PREFIX}${chapterId}`;
  const cachedContent = getCache<ChapterContent>(cacheKey);

  if (cachedContent) {
    return cachedContent;
  }

  // If not in cache, fetch from API
  // Get the active version
  const { data, error } = await supabase
    .from('chapter_contents')
    .select('*')
    .eq('chapter_id', chapterId)
    .eq('is_active', true)
    .limit(1)
    .maybeSingle();

  // If no active version, try to get the latest one (migration fallback)
  if (!data && !error) {
    const { data: latestData, error: latestError } = await supabase
      .from('chapter_contents')
      .select('*')
      .eq('chapter_id', chapterId)
      .order('version', { ascending: false })
      .limit(1)
      .single();

    if (latestError) {
      if (latestError.code === 'PGRST116') {
        // No content found, create a new one
        return createInitialChapterContent(chapterId);
      }
      console.error('Error fetching chapter content (fallback):', latestError);
      throw latestError;
    }

    // If we found a latest version but it wasn't active, return it (and maybe we should activate it? 
    // but let's just return it for read)
    return latestData;
  }

  if (error) {
    console.error('Error fetching chapter content:', error);
    throw error;
  }

  // Cache the result (2 minutes - shorter for content that changes frequently)
  setCache(cacheKey, data, 2 * 60 * 1000);

  return data;
};

/**
 * Create initial chapter content
 * @param chapterId - The ID of the chapter
 * @returns Promise with the created chapter content
 */
const createInitialChapterContent = async (chapterId: number): Promise<ChapterContent> => {
  // Initial empty content for PlateJS
  const initialContent = [
    {
      type: 'p',
      children: [{ text: '' }],
    },
  ];

  const { data, error } = await supabase
    .from('chapter_contents')
    .insert([
      {
        chapter_id: chapterId,
        content: initialContent,
        version: 1,
        is_active: true,
        word_count: 0,
        character_count: 0,
        character_count_with_spaces: 0
        // tenant_id is automatically set by RLS policies using auth.jwt()
      }
    ])
    .select()
    .single();

  if (error) {
    console.error('Error creating initial chapter content:', error);
    throw error;
  }

  // Cache the new content
  const cacheKey = `${CHAPTER_CONTENT_CACHE_PREFIX}${chapterId}`;
  setCache(cacheKey, data, 2 * 60 * 1000);

  return data;
};

/**
 * Save chapter content
 * @param chapterId - The ID of the chapter
 * @param content - The content to save
 * @returns Promise with the saved chapter content
 */
export const saveChapterContent = async (chapterId: number, content: any): Promise<ChapterContent> => {
  // Get the current version
  let currentVersion = 1;

  try {
    const existing = await getChapterContent(chapterId);
    currentVersion = (existing?.version || 0) + 1;
  } catch (error) {
    // If there's an error, start with version 1
    console.warn('Could not get current version, starting with 1:', error);
  }

  // Calculate word and character counts
  const wordCount = countWordsInPlateContent(content);
  const characterCount = countCharactersInPlateContent(content);
  const characterCountWithSpaces = countCharactersWithSpacesInPlateContent(content);

  // Deactivate all previous versions
  await supabase
    .from('chapter_contents')
    .update({ is_active: false })
    .eq('chapter_id', chapterId);

  // Insert new version
  const { data, error } = await supabase
    .from('chapter_contents')
    .insert([
      {
        chapter_id: chapterId,
        content: content,
        version: currentVersion,
        is_active: true,
        word_count: wordCount,
        character_count: characterCount,
        character_count_with_spaces: characterCountWithSpaces
        // tenant_id is automatically set by RLS policies using auth.jwt()
      }
    ])
    .select()
    .single();

  if (error) {
    console.error('Error saving chapter content:', error);
    throw error;
  }

  // Update the chapter's last_saved_at timestamp
  await supabase
    .from('chapters')
    .update({ last_saved_at: new Date().toISOString() })
    .eq('id', chapterId);

  // Update cache with new content
  const cacheKey = `${CHAPTER_CONTENT_CACHE_PREFIX}${chapterId}`;
  setCache(cacheKey, data, 2 * 60 * 1000);

  // Update Word Count History (Fire and forget to not block save)
  getChapterById(chapterId).then(chapter => {
    if (chapter && chapter.story_id) {
      // updateWordCountHistory({
      //   date: new Date().toISOString().split('T')[0],
      //   story_id: chapter.story_id,
      //   chapter_id: chapterId,
      //   word_count: wordCount
      // }).catch(err => console.error('Failed to update word count history:', err));
    }
  }).catch(err => console.error('Failed to get chapter for history update:', err));

  return data;
};










/**
 * Delete a chapter
 * @param chapterId - The ID of the chapter to delete
 * @returns Promise with success status
 */
export const deleteChapter = async (chapterId: number): Promise<void> => {
  const { error } = await supabase
    .from('chapters')
    .delete()
    .eq('id', chapterId);

  if (error) {
    console.error('Error deleting chapter:', error);
    throw error;
  }
};

/**
 * Delete multiple chapters
 * @param chapterIds - The IDs of the chapters to delete
 * @returns Promise with success status
 */
export const deleteChapters = async (chapterIds: number[]): Promise<void> => {
  if (chapterIds.length === 0) return;

  const { error } = await supabase
    .from('chapters')
    .delete()
    .in('id', chapterIds);

  if (error) {
    console.error('Error deleting chapters:', error);
    throw error;
  }
};

/**
 * Get chapter statistics
 * @param chapterId - The ID of the chapter
 * @returns Promise with the chapter statistics
 */
export const getChapterStats = async (chapterId: number): Promise<ChapterStats> => {
  // Try to get from cache first
  const cacheKey = `${CHAPTER_STATS_CACHE_PREFIX}${chapterId}`;
  const cachedStats = getCache<ChapterStats>(cacheKey);

  if (cachedStats) {
    return cachedStats;
  }

  // If not in cache, fetch from API
  const { data, error } = await supabase
    .from('chapter_stats')
    .select('*')
    .eq('chapter_id', chapterId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // No stats found - this is normal for new chapters
      console.log('No stats found for chapter ID:', chapterId);
      return {
        id: 0,
        chapter_id: chapterId,
        tenant_id: '',
        word_count: 0,
        character_count: 0,
        reading_time: 0,
        last_updated_at: new Date().toISOString()
      };
    }

    console.error('Error fetching chapter stats:', error);
    throw error;
  }

  // Cache the result (1 minute - stats change frequently)
  setCache(cacheKey, data, 60 * 1000);

  return data;
};

/**
 * Update chapter statistics
 * @param chapterIdOrStats - Either a chapter ID or a stats object with chapter_id
 * @param statsObject - Optional stats object if first parameter is chapter ID
 * @returns Promise with the updated statistics
 */
export const updateChapterStats = async (
  chapterIdOrStats: number | Omit<ChapterStats, 'id' | 'tenant_id'>,
  statsObject?: { word_count: number; character_count: number; reading_time: number }
): Promise<ChapterStats> => {
  // Determine if we're using the newer (chapterId, stats) pattern or the older (stats) pattern
  let stats: Omit<ChapterStats, 'id' | 'tenant_id'>;

  if (typeof chapterIdOrStats === 'number' && statsObject) {
    // New pattern: called with (chapterId, statsObject)
    stats = {
      chapter_id: chapterIdOrStats,
      word_count: statsObject.word_count,
      character_count: statsObject.character_count,
      reading_time: statsObject.reading_time,
      last_updated_at: new Date().toISOString()
    };
  } else if (typeof chapterIdOrStats === 'object' && 'chapter_id' in chapterIdOrStats) {
    // Old pattern: called with (stats)
    stats = chapterIdOrStats;
  } else {
    console.error('Invalid arguments to updateChapterStats:', { chapterIdOrStats, statsObject });
    throw new Error('Invalid arguments to updateChapterStats');
  }

  console.log('Updating chapter stats:', stats);

  try {
    // First check if stats already exist for this chapter
    const { data: existingData, error: fetchError } = await supabase
      .from('chapter_stats')
      .select('id')
      .eq('chapter_id', stats.chapter_id)
      .maybeSingle();

    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error('Error checking existing chapter stats:', fetchError);
      throw fetchError;
    }

    let result;

    if (existingData?.id) {
      // Update existing stats
      const { data, error } = await supabase
        .from('chapter_stats')
        .update({
          word_count: stats.word_count,
          character_count: stats.character_count,
          reading_time: stats.reading_time,
          last_updated_at: new Date().toISOString()
        })
        .eq('id', existingData.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating chapter stats:', error);
        throw error;
      }

      result = data;
    } else {
      // Insert new stats
      const { data, error } = await supabase
        .from('chapter_stats')
        .insert([{
          chapter_id: stats.chapter_id,
          word_count: stats.word_count,
          character_count: stats.character_count,
          reading_time: stats.reading_time,
          last_updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) {
        console.error('Error inserting chapter stats:', error);
        throw error;
      }

      result = data;
    }



    // Update cache
    const cacheKey = `${CHAPTER_STATS_CACHE_PREFIX}${stats.chapter_id}`;
    setCache(cacheKey, result, 60 * 1000);



    return result;
  } catch (error) {
    console.error('Error in updateChapterStats:', error);
    throw error;
  }
};

/**
 * Get export configurations for a story
 * @param storyId - The ID of the story
 * @returns Promise with an array of export configurations
 */
export const getExportConfigurations = async (storyId: number): Promise<ExportConfiguration[]> => {
  // Try to get from cache first
  const cacheKey = `${EXPORT_CONFIG_CACHE_PREFIX}${storyId}`;
  const cachedConfigs = getCache<ExportConfiguration[]>(cacheKey);

  if (cachedConfigs) {
    return cachedConfigs;
  }

  // If not in cache, fetch from API
  const { data, error } = await supabase
    .from('export_configurations')
    .select('*')
    .eq('story_id', storyId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching export configurations:', error);
    throw error;
  }

  // Cache the result (5 minutes)
  setCache(cacheKey, data || [], 5 * 60 * 1000);

  return data || [];
};

/**
 * Create or update an export configuration
 * @param config - The export configuration data
 * @returns Promise with the created or updated configuration
 */
export const saveExportConfiguration = async (
  config: Partial<ExportConfiguration> & { story_id: number }
): Promise<ExportConfiguration> => {
  const { data, error } = config.id
    // Update existing config
    ? await supabase
      .from('export_configurations')
      .update({
        name: config.name,
        format: config.format,
        configuration: config.configuration,
        updated_at: new Date().toISOString()
      })
      .eq('id', config.id)
      .select()
      .single()
    // Create new config
    : await supabase
      .from('export_configurations')
      .insert([{
        story_id: config.story_id,
        name: config.name,
        format: config.format,
        configuration: config.configuration || {}
      }])
      .select()
      .single();

  if (error) {
    console.error('Error saving export configuration:', error);
    throw error;
  }

  // Clear cache
  clearCache(`${EXPORT_CONFIG_CACHE_PREFIX}${config.story_id}`);

  return data;
};

/**
 * Delete an export configuration
 * @param configId - The ID of the configuration to delete
 * @param storyId - The story ID (for cache clearing)
 */
export const deleteExportConfiguration = async (configId: number, storyId: number): Promise<void> => {
  const { error } = await supabase
    .from('export_configurations')
    .delete()
    .eq('id', configId);

  if (error) {
    console.error('Error deleting export configuration:', error);
    throw error;
  }

  // Clear cache
  clearCache(`${EXPORT_CONFIG_CACHE_PREFIX}${storyId}`);
};

/**
 * Create the word_count_goals table with proper schema and RLS policies
 */
export async function createWordCountGoalsTable(): Promise<boolean> {
  try {
    console.log('Creating word_count_goals table...');

    // Create the table
    const { error: tableError } = await supabase.rpc('exec_sql', {
      sql: `
        -- Create word_count_goals table
        CREATE TABLE IF NOT EXISTS word_count_goals (
          id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
          user_id UUID NOT NULL REFERENCES auth.users(id) ON DELETE CASCADE,
          tenant_id INT8 NOT NULL,
          name TEXT NOT NULL,
          description TEXT,
          goal_type TEXT NOT NULL CHECK (goal_type IN ('chapter', 'story', 'project')),
          period TEXT NOT NULL CHECK (period IN ('daily', 'weekly', 'monthly', 'total')),
          target_words INTEGER NOT NULL CHECK (target_words > 0),
          current_words INTEGER DEFAULT 0 CHECK (current_words >= 0),
          start_date DATE NOT NULL DEFAULT CURRENT_DATE,
          end_date DATE,
          deadline DATE,
          chapter_id INT8 REFERENCES chapters(id) ON DELETE CASCADE,
          story_id INT8 REFERENCES stories(id) ON DELETE CASCADE,
          project_id INT8 REFERENCES projects(id) ON DELETE CASCADE,
          completed BOOLEAN DEFAULT FALSE,
          completed_date TIMESTAMPTZ,
          created_at TIMESTAMPTZ DEFAULT NOW(),
          updated_at TIMESTAMPTZ DEFAULT NOW(),

          -- Constraints
          CONSTRAINT valid_goal_association CHECK (
            (goal_type = 'chapter' AND chapter_id IS NOT NULL AND story_id IS NULL AND project_id IS NULL) OR
            (goal_type = 'story' AND story_id IS NOT NULL AND chapter_id IS NULL AND project_id IS NULL) OR
            (goal_type = 'project' AND project_id IS NOT NULL AND chapter_id IS NULL AND story_id IS NULL)
          ),
          CONSTRAINT valid_date_range CHECK (end_date IS NULL OR end_date >= start_date),
          CONSTRAINT valid_deadline CHECK (deadline IS NULL OR deadline >= start_date)
        );

        -- Create indexes for better performance
        CREATE INDEX IF NOT EXISTS idx_word_count_goals_user_id ON word_count_goals(user_id);
        CREATE INDEX IF NOT EXISTS idx_word_count_goals_tenant_id ON word_count_goals(tenant_id);
        CREATE INDEX IF NOT EXISTS idx_word_count_goals_goal_type ON word_count_goals(goal_type);
        CREATE INDEX IF NOT EXISTS idx_word_count_goals_chapter_id ON word_count_goals(chapter_id);
        CREATE INDEX IF NOT EXISTS idx_word_count_goals_story_id ON word_count_goals(story_id);
        CREATE INDEX IF NOT EXISTS idx_word_count_goals_project_id ON word_count_goals(project_id);
        CREATE INDEX IF NOT EXISTS idx_word_count_goals_completed ON word_count_goals(completed);
        CREATE INDEX IF NOT EXISTS idx_word_count_goals_deadline ON word_count_goals(deadline);
      `
    });

    if (tableError) {
      console.error('Error creating table:', tableError);
      throw tableError;
    }

    // Create RLS policies
    const { error: rlsError } = await supabase.rpc('exec_sql', {
      sql: `
        -- Enable RLS
        ALTER TABLE word_count_goals ENABLE ROW LEVEL SECURITY;

        -- Drop existing policies if they exist
        DROP POLICY IF EXISTS "Users can view their own word count goals" ON word_count_goals;
        DROP POLICY IF EXISTS "Users can insert their own word count goals" ON word_count_goals;
        DROP POLICY IF EXISTS "Users can update their own word count goals" ON word_count_goals;
        DROP POLICY IF EXISTS "Users can delete their own word count goals" ON word_count_goals;

        -- Create RLS policies
        CREATE POLICY "Users can view their own word count goals"
          ON word_count_goals FOR SELECT
          USING (auth.uid() = user_id);

        CREATE POLICY "Users can insert their own word count goals"
          ON word_count_goals FOR INSERT
          WITH CHECK (auth.uid() IS NOT NULL);

        CREATE POLICY "Users can update their own word count goals"
          ON word_count_goals FOR UPDATE
          USING (auth.uid() = user_id)
          WITH CHECK (auth.uid() = user_id);

        CREATE POLICY "Users can delete their own word count goals"
          ON word_count_goals FOR DELETE
          USING (auth.uid() = user_id);
      `
    });

    if (rlsError) {
      console.error('Error creating RLS policies:', rlsError);
      throw rlsError;
    }

    // Create triggers for automatic user_id and tenant_id setting
    const { error: triggerError } = await supabase.rpc('exec_sql', {
      sql: `
        -- Create trigger function to set user_id and tenant_id
        CREATE OR REPLACE FUNCTION set_word_count_goal_metadata()
        RETURNS TRIGGER AS $$
        DECLARE
          current_tenant_id INT8;
        BEGIN
          -- Set user_id from auth context
          NEW.user_id = auth.uid();

          -- Get tenant_id from user_tenants table
          SELECT tenant_id INTO current_tenant_id
          FROM user_tenants
          WHERE user_id = auth.uid()
          LIMIT 1;

          IF current_tenant_id IS NOT NULL THEN
            NEW.tenant_id = current_tenant_id;
          ELSE
            RAISE EXCEPTION 'No tenant association found for user';
          END IF;

          -- Set updated_at for updates
          IF TG_OP = 'UPDATE' THEN
            NEW.updated_at = NOW();
          END IF;

          RETURN NEW;
        END;
        $$ LANGUAGE plpgsql SECURITY DEFINER;

        -- Create triggers
        DROP TRIGGER IF EXISTS set_word_count_goal_metadata_trigger ON word_count_goals;
        CREATE TRIGGER set_word_count_goal_metadata_trigger
          BEFORE INSERT OR UPDATE ON word_count_goals
          FOR EACH ROW EXECUTE FUNCTION set_word_count_goal_metadata();
      `
    });

    if (triggerError) {
      console.error('Error creating triggers:', triggerError);
      throw triggerError;
    }

    console.log('Successfully created word_count_goals table with RLS policies and triggers');
    return true;
  } catch (error) {
    console.error('Error creating word_count_goals table:', error);
    return false;
  }
}

/**
 * Create multiple chapters in bulk using RPC
 * @param storyId - The ID of the story
 * @param chapters - Array of chapter objects containing name and outline
 * @returns Promise with the result of the RPC call
 */
export const createChaptersBulk = async (storyId: number, chapters: any[]): Promise<any> => {
  const { data, error } = await supabase.rpc('create_chapters_bulk', {
    story_id: storyId,
    chapters: chapters
  });

  if (error) {
    console.error('Error creating chapters bulk:', error);
    throw error;
  }

  // Clear cache for this story
  clearCache(`${CHAPTERS_CACHE_PREFIX}${storyId}`);

  return data;
};